import { Children, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ, formatTimeAgo } from '@utils/dateUtils';
import { reportRoute, type IInvestigationReportType } from '@apis/investigationReports';
import styles from './RecentCardShell.module.css';

interface IRecentCardShellProps {
  reportType: IInvestigationReportType;
  reportId: string;
  siteId: string;
  createdAtIso: string;
  /**
   * Display title for the prominent top-left line. Comes from the
   * archived record's `title` field (the user can rename via the
   * report page). When absent, the rail falls back to the report
   * type's i18n title so legacy entries still render. The
   * bottom-row meta label always shows the type label so users can
   * still identify the kind of report after rename.
   */
  title?: string;
  /**
   * Per-type middle slot. Each report type's RecentCard renders its own
   * entity row here (e.g. `name - nick · date` for Daily Route, plain
   * subtitle for the default card). The shell does not interpret it.
   */
  children: ReactNode;
}

/**
 * Shared visual chrome for every entry in the Recent Analyses rail. Owns
 * the link, the title (top-left), the relative time (top-right), and
 * the meta row's frame — the per-type RecentCard fills the meta row's
 * children. Keeping the chrome in one place ensures all cards share
 * padding, hover, divider, and timestamp formatting without duplication.
 */
export function RecentCardShell({
  reportType,
  reportId,
  siteId,
  createdAtIso,
  title,
  children,
}: IRecentCardShellProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ja' ? 'ja' : 'en';
  // tz is sourced from the working-site selector — every rendering of
  // this shell happens inside a site context (the rail lives on the
  // analytics page, which is mounted under `/analytics/:siteId/*`). The
  // `?? APP_DEFAULT_TZ` covers the brief bootstrap window before the
  // working-site has hydrated into redux.
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  // The i18n catalogue covers d-orca's UI report types; the archive
  // enum has one extra (`scissorLiftReportExport`). Pass an empty
  // default so a missing key surfaces visibly during dev rather than
  // throwing — falls back to the record's title in that case.
  const typeLabel = t(`analytics.cards.${reportType}.title`, { defaultValue: '' });
  // `Children.toArray` drops the null/undefined/false a card emits when it
  // opts out, but KEEPS strings — including the `''` that `{label && …}`
  // yields for an empty label — so empty text has to be filtered too.
  const hasMeta = Children.toArray(children).some(
    (child) => typeof child !== 'string' || child.trim().length > 0,
  );
  return (
    <Link to={reportRoute(siteId, reportType, reportId)} className={styles.link}>
      <span className={styles.cardTitle}>{title || typeLabel}</span>
      <span className={styles.timeAgo}>{formatTimeAgo(createdAtIso, t, lang, tz)}</span>
      <span className={styles.meta}>
        <span className={styles.metaLabel}>{typeLabel}</span>
        {/* The divider SEPARATES the type label from the card's own meta —
            with nothing after it, it renders as a stray 1px rule. Cards now
            legitimately contribute nothing (a contractorAnalysis with no
            profession, a window-keyed record with no window), so the shell
            has to ask rather than assume. */}
        {hasMeta && <span className={styles.divider} aria-hidden="true" />}
        {children}
      </span>
    </Link>
  );
}
